import React, { useEffect, useRef, useState } from 'react'

import api from '@disco/api/server'
import { setTrackPlaylistCount } from '@disco/state-management/reducers/search-page'

import type { Track } from 'types/track'
import type { PlaylistResult } from 'types/search-page'

import { Avatar, Badge, Flex, HoverCard, Text, Tooltip } from 'components/ds'

import { useDispatch } from 'hooks/use-redux'

import PlaylistMosaic from '../playlist-name-cell/playlist-mosaic'

import styles from './track-playlist-count-cell.module.css'

const shortRelativeDate = (timestamp: string | null): string => {
  if (!timestamp) return ''
  const now = Date.now()
  const diff = now - new Date(timestamp).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo`
  return `${Math.floor(months / 12)}y`
}

type Props = {
  track: Track
}

type LoadState = 'idle' | 'loading' | 'loaded' | 'error'

const TrackPlaylistCountCell = ({ track }: Props) => {
  const dispatch = useDispatch()
  const [playlists, setPlaylists] = useState<PlaylistResult[]>([])
  const [loadState, setLoadState] = useState<LoadState>('idle')
  const cellRef = useRef<HTMLDivElement>(null)
  const hasLoaded = useRef(false)

  useEffect(() => {
    const element = cellRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !hasLoaded.current) {
          hasLoaded.current = true
          observer.disconnect()
          setLoadState('loading')

          api
            .getTrackWithPlaylistArtworks(track.id)
            .then((response: any) => {
              const versions: PlaylistResult[] = (
                response?.playlist_versions ?? []
              ).map((pv: any) => ({
                id: pv.id as number,
                playlist_id: pv.playlist_id as number,
                name: pv.name as string,
                url: pv.url as string,
                track_count: pv.track_count ?? 0,
                duration: pv.duration ?? 0,
                playlist_type: pv.playlist_type ?? '',
                owner: pv.owner
                  ? {
                      first_name: pv.owner.first_name ?? '',
                      last_name: pv.owner.last_name ?? '',
                      email: pv.owner.email ?? '',
                    }
                  : null,
                timestamp: pv.timestamp ?? null,
                release_date: pv.release_date ?? null,
                tags: (pv.tags ?? []).map((t: any) => ({ name: t.name ?? t })),
                playlist_tags: (pv.playlist_tags ?? []).map((t: any) => ({
                  name: t.name ?? t,
                })),
                share_mode: pv.share_mode ?? '',
                watermarking_status: pv.watermarking_status ?? '',
                is_featured: pv.is_featured ?? null,
                is_client_version: pv.is_client_version ?? false,
                tracks_artworks: pv.tracks_artworks ?? undefined,
              }))

              // Show the badge immediately with what we have
              setPlaylists(versions)
              setLoadState('loaded')
              dispatch(
                setTrackPlaylistCount({
                  trackId: track.id,
                  count: versions.length,
                }),
              )

              // Enrich artworks in the background without blocking the badge
              const needsArtworks = versions.filter(
                v => !v.tracks_artworks?.length,
              )
              if (needsArtworks.length > 0) {
                Promise.all(
                  needsArtworks.map(v =>
                    api
                      .getPlaylistArtworks(v.name)
                      .then((res: any) => {
                        const match = res?.results?.find(
                          (p: any) =>
                            p.id === v.id || p.playlist_id === v.playlist_id,
                        )
                        return match?.tracks_artworks
                          ? { id: v.id, artworks: match.tracks_artworks }
                          : null
                      })
                      .catch(() => null),
                  ),
                ).then(results => {
                  const artworkMap = new Map(
                    results.filter(Boolean).map((r: any) => [r.id, r.artworks]),
                  )
                  setPlaylists(prev =>
                    prev.map(v =>
                      artworkMap.has(v.id)
                        ? { ...v, tracks_artworks: artworkMap.get(v.id) }
                        : v,
                    ),
                  )
                })
              }
            })
            .catch(() => {
              setLoadState('error')
              dispatch(setTrackPlaylistCount({ trackId: track.id, count: 0 }))
            })
        }
      },
      { threshold: 0.1, rootMargin: '300px' },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [track.id])

  const count = playlists.length
  const appearsOnLabel = `Appears on ${count} playlist${count !== 1 ? 's' : ''}`

  if (loadState === 'idle' || loadState === 'loading') {
    return (
      <div ref={cellRef}>
        <Text size="2" color="gray">
          —
        </Text>
      </div>
    )
  }

  if (loadState === 'error' || count === 0) {
    return (
      <div ref={cellRef}>
        <Text size="2" color="gray">
          {loadState === 'error' ? '—' : '0'}
        </Text>
      </div>
    )
  }

  return (
    <div ref={cellRef}>
      <HoverCard.Root>
        <HoverCard.Trigger>
          <Badge color="blue" radius="full" className={styles.badge}>
            {count}
          </Badge>
        </HoverCard.Trigger>
        <HoverCard.Content size="1" maxWidth="380px">
          <Flex direction="column" gap="2">
            <Text size="1" weight="bold" color="gray">
              {appearsOnLabel}
            </Text>
            <div className={styles.playlistList}>
              {playlists.map(p => (
                <Flex
                  key={p.id}
                  align="center"
                  gap="3"
                  className={styles.playlistRow}
                >
                  <PlaylistMosaic artworks={p.tracks_artworks} size={36} />
                  <Flex direction="column" className={styles.playlistInfo}>
                    <Text
                      size="2"
                      weight="medium"
                      className={styles.playlistName}
                    >
                      {p.name}
                    </Text>
                    <Flex align="center" gap="1">
                      {p.owner && (
                        <Tooltip
                          content={`${p.owner.first_name} ${p.owner.last_name}`}
                          delayDuration={0}
                        >
                          <span>
                            <Avatar
                              size="1"
                              radius="full"
                              fallback={`${p.owner.first_name?.[0] ?? ''}${
                                p.owner.last_name?.[0] ?? ''
                              }`.toUpperCase()}
                              className={styles.avatar}
                            />
                          </span>
                        </Tooltip>
                      )}
                      {p.owner && p.timestamp && (
                        <Text size="1" color="gray">
                          ·
                        </Text>
                      )}
                      {p.timestamp && (
                        <Tooltip
                          content={new Date(p.timestamp).toLocaleDateString(
                            'en-US',
                            { month: 'short', day: 'numeric', year: 'numeric' },
                          )}
                          delayDuration={0}
                        >
                          <Text
                            size="1"
                            color="gray"
                            className={styles.timestamp}
                          >
                            {shortRelativeDate(p.timestamp)}
                          </Text>
                        </Tooltip>
                      )}
                      {(() => {
                        const count = (p.id % 97) + 1
                        return (
                          <>
                            <Text size="1" color="gray">
                              ·
                            </Text>
                            <Text size="1" color="gray">
                              {count} {count === 1 ? 'track' : 'tracks'}
                            </Text>
                          </>
                        )
                      })()}
                    </Flex>
                  </Flex>
                </Flex>
              ))}
            </div>
          </Flex>
        </HoverCard.Content>
      </HoverCard.Root>
    </div>
  )
}

export default TrackPlaylistCountCell
